import {
  Badge,
  Button,
  buttonVariants,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  confirm,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import {
  RiArrowDownLine,
  RiArrowUpLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiDragMove2Line,
  RiExternalLinkLine,
  RiEyeLine,
  RiEyeOffLine,
  RiFilter3Line,
  RiSkipBackLine,
  RiSkipForwardLine,
} from "@remixicon/react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { usePosWarehousesQuery } from "@/modules/cf-feed/presentation/api/queries";
import type {
  AdminCatalogItem,
  AdminCatalogProduct,
} from "@/modules/cf-feed/presentation/api/types";
import { updateCatalogSelection } from "@/modules/cf-feed/presentation/lib/catalog-add";
import { getMonitorQueueNeighbors } from "@/modules/cf-feed/presentation/lib/feed-product-monitor-display-selection";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { FeedWorkspaceNavigation } from "@/modules/cf-feed/presentation/ui/FeedWorkspaceNavigation";
import type {
  PriceInput,
  WorkspaceCommand,
  WorkspaceSnapshot,
  WorkspaceVariant,
} from "../../domain/contracts/live-workspace";
import {
  WorkspaceRequestError,
  workspaceErrorMessage,
  workspaceRequest,
} from "../api/workspace-client";
import { LiveOverlay } from "../ui/LiveOverlay";
import { WorkspaceCatalogCards } from "../ui/WorkspaceCatalogCards";
import { WorkspaceCatalogPriceDialog } from "../ui/WorkspaceCatalogPriceDialog";
import { WorkspaceOrders } from "../ui/WorkspaceOrders";
import {
  WorkspaceProductCard,
  type WorkspaceProductCardAction,
} from "../ui/WorkspaceProductCard";
import {
  kip,
  WorkspaceProductEditor,
  WorkspaceSelect,
} from "../ui/WorkspaceProductEditor";
import { WorkspaceProductSets } from "../ui/WorkspaceProductSets";
import {
  WorkspaceDisplaySettings,
  WorkspacePreparation,
} from "../ui/WorkspaceSetup";

type CommandResult = {
  revision: number;
  draftId?: string;
  failed?: Array<{ id: string; code: string }>;
  added?: string[];
  prepared?: string[];
  skipped?: Array<{
    variantId: string;
    code: "PRODUCT_ALREADY_IN_WORKSPACE";
  }>;
};
const pageSize = 20;
function conflictSummary(value: unknown): string {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "—";
  const row = value as Record<string, unknown>;
  const parts = [
    typeof row.cfCode === "string" ? row.cfCode : null,
    typeof row.price === "number" ? kip(row.price) : null,
    row.discountPercent != null || row.listDiscountPercent != null
      ? `ຫຼຸດ ${row.discountPercent ?? row.listDiscountPercent}%`
      : null,
    row.discountFixedLak != null || row.listDiscountFixedLak != null
      ? `ຫຼຸດ ${kip(Number(row.discountFixedLak ?? row.listDiscountFixedLak))}`
      : null,
  ].filter(Boolean);
  return parts.join(" · ") || "ບໍ່ຫຼຸດລາຄາ";
}
function useDelayed<T>(value: T) {
  const [delayed, setDelayed] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDelayed(value), 300);
    return () => clearTimeout(timer);
  }, [value]);
  return delayed;
}

export function LiveWorkspacePage({
  kind,
  id,
}: {
  kind: "draft" | "feed";
  id: string;
}) {
  const resolved = useQuery({
    queryKey: ["workspace-resolve", kind, id],
    queryFn: () =>
      workspaceRequest<{ id: string }>(
        `/resolve?kind=${kind}&id=${encodeURIComponent(id)}`,
      ),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return (
    <>
      <Header />
      <Main className="space-y-4">
        {kind === "feed" ? (
          <FeedWorkspaceNavigation feedId={id} activeTab="products" />
        ) : (
          <a
            className="inline-flex min-h-11 items-center text-sm underline"
            href="/app/live"
          >
            ກັບໄປລາຍການ Live / Feed
          </a>
        )}
        {resolved.data ? (
          <WorkspaceControl key={resolved.data.id} id={resolved.data.id} />
        ) : (
          <output>
            {resolved.isError
              ? workspaceErrorMessage(resolved.error)
              : "ກຳລັງໂຫຼດງານ…"}
          </output>
        )}
      </Main>
    </>
  );
}

function WorkspaceControl({ id }: { id: string }) {
  const client = useQueryClient();
  const { has } = usePermissions();
  const canManage = has("live:manage");
  const [tab, setTab] = useState("products");
  const [managingQueue, setManagingQueue] = useState(false);
  const [q, setQ] = useState("");
  const search = useDelayed(q);
  const [filters, setFilters] = useState({
    sale: "all",
    readiness: "all",
    discount: "all",
    queue: "all",
  });
  const [selected, setSelected] = useState<WorkspaceVariant[]>([]);
  const [editor, setEditor] = useState<{
    products: WorkspaceVariant[];
    global: boolean;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [failed, setFailed] = useState<Array<{ id: string; code: string }>>([]);
  const [retryCommand, setRetryCommand] = useState<WorkspaceCommand | null>(
    null,
  );
  const commandLock = useRef(false);
  const params = new URLSearchParams({ q: search, limit: String(pageSize) });
  for (const [key, value] of Object.entries(filters))
    if (value !== "all") params.set(key, value);
  const query = useInfiniteQuery({
    queryKey: ["workspace", id, params.toString()],
    queryFn: ({ signal, pageParam }) => {
      const pageParams = new URLSearchParams(params);
      pageParams.set("offset", String(pageParam));
      return workspaceRequest<WorkspaceSnapshot>(
        `/${id}?${pageParams}`,
        undefined,
        signal,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = new Set(
        pages.flatMap((page) =>
          page.products.map((product) => product.groupId),
        ),
      ).size;
      return loaded < lastPage.total ? loaded : undefined;
    },
    retry: false,
  });
  const workspace = query.data?.pages[0]
    ? {
        ...query.data.pages[0],
        products: [
          ...new Map(
            query.data.pages
              .flatMap((page) => page.products)
              .map((product) => [product.id, product]),
          ).values(),
        ],
      }
    : undefined;
  const {
    scrollRootRef: productsScrollRootRef,
    sentinelRef: productsSentinelRef,
  } = useBoundedInfiniteScroll({
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    onLoadMore: () => void query.fetchNextPage(),
  });
  const command = useMutation({
    mutationFn: (input: WorkspaceCommand) =>
      workspaceRequest<CommandResult>(`/${id}/commands`, input),
    retry: (count, error) =>
      count < 1 && !(error instanceof WorkspaceRequestError),
    onSuccess: async (result) => {
      setError(null);
      setRetryCommand(null);
      setFailed(result.failed ?? []);
      await client.invalidateQueries({ queryKey: ["workspace", id] });
      await client.invalidateQueries({ queryKey: ["workspace-catalog", id] });
      await client.invalidateQueries({
        queryKey: ["workspace-display-token", id],
      });
      if (result.draftId)
        window.location.assign(
          `/app/live/new?draftId=${encodeURIComponent(result.draftId)}`,
        );
    },
  });
  const runCommand = async (
    action: WorkspaceCommand["action"],
    previous?: WorkspaceCommand,
  ): Promise<CommandResult | false> => {
    if (!workspace || commandLock.current || !canManage) return false;
    commandLock.current = true;
    const input = previous ?? {
      key: crypto.randomUUID(),
      revision: workspace.revision,
      action,
    };
    try {
      const result = await command.mutateAsync(input);
      if (
        result.failed?.length &&
        (action.type === "products" || action.type === "remove-products")
      ) {
        setSelected(
          selected.filter((p) => result.failed?.some((f) => f.id === p.id)),
        );
        setEditor(
          (previous) =>
            previous && {
              ...previous,
              products: previous.products.filter((p) =>
                result.failed?.some((f) => f.id === p.id),
              ),
            },
        );
      }
      if (!result.failed?.length && action.type === "products") {
        setSelected([]);
      }
      return result;
    } catch (error) {
      setError(error);
      if (!(error instanceof WorkspaceRequestError)) setRetryCommand(input);
      if (
        error instanceof WorkspaceRequestError &&
        error.code === "REVISION_CONFLICT"
      )
        await client.invalidateQueries({ queryKey: ["workspace", id] });
      return false;
    } finally {
      commandLock.current = false;
    }
  };
  const save: Save = async (action, previous) => {
    const result = await runCommand(action, previous);
    return Boolean(result && !result.failed?.length);
  };
  const applyProductSet = (templateId: string) =>
    runCommand({ type: "apply-product-set", templateId });
  if (!workspace)
    return (
      <div className="space-y-3">
        <output>
          {query.isError ? workspaceErrorMessage(query.error) : "ກຳລັງໂຫຼດ…"}
        </output>
        {query.isError && (
          <Button onClick={() => void query.refetch()}>ລອງໃໝ່</Button>
        )}
      </div>
    );
  const busy = command.isPending || !canManage;
  const groups = new Map<string, WorkspaceVariant[]>();
  for (const product of workspace.products) {
    const variants = groups.get(product.groupId);
    if (variants) variants.push(product);
    else groups.set(product.groupId, [product]);
  }
  const queuePositions = new Map(
    workspace.queue.map((product, index) => [product.id, index + 1]),
  );
  const selectedIds = new Set(selected.map((product) => product.id));
  const active = workspace.queue.find(
    (p) => p.id === workspace.state.activeProductId,
  );
  const activeIndex = workspace.queue.findIndex((p) => p.id === active?.id);
  const { previous } = getMonitorQueueNeighbors(
    workspace.queue,
    active?.id,
    workspace.state.excludedProductIds,
  );
  const next = workspace.queue
    .slice(activeIndex + 1)
    .find((p) => !workspace.state.excludedProductIds.includes(p.id));
  const filterCount = Object.values(filters).filter((v) => v !== "all").length;
  const removeProducts = async (products: WorkspaceVariant[]) => {
    if (products.length === 0 || busy) return;
    const ok = await confirm({
      title: `ນຳສິນຄ້າ ${products.length} ລາຍການອອກ?`,
      description: "ສິນຄ້າຈະຖືກນຳອອກຈາກ Feed/Live ນີ້. ລາຍການທີ່ມີອໍເດີ້ແລ້ວຈະບໍ່ຖືກລົບ.",
      actionText: "ນຳອອກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    const removed = await save({
      type: "remove-products",
      ids: products.map((product) => product.id),
    });
    if (removed) {
      const removedIds = new Set(products.map((product) => product.id));
      setSelected((previous) =>
        previous.filter((product) => !removedIds.has(product.id)),
      );
    }
  };
  const preview = (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">ຕົວຢ່າງ OBS</h2>
        <Badge variant="outline">
          {workspace.state.settings.orientation === "portrait"
            ? "Widget ແນວຕັ້ງ"
            : "Widget ແນວນອນ"}
        </Badge>
      </div>
      <p className="text-muted-foreground text-sm">
        {active ? `ກຳລັງສະແດງ: ${active.title}` : "ຈໍຖືກເຊື່ອງ — ເລືອກສິນຄ້າແລ້ວກົດຂຶ້ນຈໍ"}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          className="min-h-11 flex-1"
          disabled={busy || !previous}
          title={previous?.title}
          onClick={() =>
            previous && void save({ type: "display", productId: previous.id })
          }
        >
          <RiSkipBackLine size={18} />
          ກ່ອນໜ້າ
        </Button>
        <Button
          className="min-h-11 flex-1"
          disabled={busy || !next}
          onClick={() => void save({ type: "next" })}
        >
          <RiSkipForwardLine size={18} />
          ຖັດໄປ
        </Button>
        <Button
          variant="outline"
          className="min-h-11 flex-1"
          disabled={busy || !active}
          onClick={() => void save({ type: "display", productId: null })}
        >
          <RiEyeOffLine size={18} />
          ເຊື່ອງຈໍ
        </Button>
        <Button
          variant="outline"
          className="min-h-11 w-full"
          disabled={busy || !active}
          onClick={() => void save({ type: "skip" })}
        >
          ຂ້າມ / ນຳອອກຈາກຄິວ
        </Button>
      </div>
      <p className="break-words text-sm">
        <span className="text-muted-foreground">ຄິວຖັດໄປ: </span>
        {next?.title ?? "ບໍ່ມີຄິວຖັດໄປ"}
      </p>
      <div className="overflow-hidden rounded-xl border bg-[length:20px_20px] bg-[repeating-conic-gradient(#80808020_0%_25%,transparent_0%_50%)]">
        <LiveOverlay display={workspace.preview} />
      </div>
      {workspace.feedId && (
        <div className="space-y-1">
          <a
            className={`${buttonVariants({ variant: "outline" })} min-h-11`}
            href={`/app/feeds/${encodeURIComponent(workspace.feedId)}/monitor-display`}
            target="_blank"
            rel="noreferrer"
          >
            <RiEyeLine size={18} />
            ເປີດໜ້າຈໍສິນຄ້າແບບເດີມ
          </a>
          <p className="text-muted-foreground text-xs">
            ຈໍເດີມ ແລະ OBS ໃຊ້ສິນຄ້າທີ່ເລືອກຈາກຄິວນີ້ຮ່ວມກັນ
          </p>
        </div>
      )}
      <details>
        <summary className="cursor-pointer py-3">
          ຄິວຂາຍທັງໝົດ ({workspace.queue.length})
        </summary>
        <ol className="max-h-64 space-y-1 overflow-y-auto">
          {workspace.queue.map((p, i) => (
            <li key={p.id}>
              <Button
                variant={p.id === active?.id ? "secondary" : "ghost"}
                className="h-auto min-h-11 w-full justify-start whitespace-normal text-left"
                disabled={busy}
                onClick={() => void save({ type: "display", productId: p.id })}
              >
                {i + 1}. {p.title}
                {workspace.state.excludedProductIds.includes(p.id)
                  ? " · ຂ້າມແລ້ວ"
                  : ""}
              </Button>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-muted-foreground text-sm">
            {workspace.feedId
              ? "ງານຂາຍ Facebook · Live / Feed"
              : "ກຽມການຂາຍລ່ວງໜ້າ"}
          </p>
          <h1 className="font-semibold text-2xl">
            {workspace.title || "ງານຂາຍໃໝ່"}
          </h1>
          <output
            aria-live="polite"
            className="mt-1 flex items-center gap-1 text-muted-foreground text-sm"
          >
            <RiCheckLine size={16} />
            {command.isPending
              ? "ກຳລັງບັນທຶກ…"
              : query.isError
                ? "ຂາດການເຊື່ອມຕໍ່ — ກຳລັງລອງໃໝ່"
                : "ບັນທຶກເທິງ server ແລ້ວ"}
          </output>
        </div>
        <Button
          className="min-h-11"
          variant={workspace.ordering ? "destructive" : "default"}
          disabled={busy || !workspace.feedId}
          onClick={() =>
            void save({ type: "ordering", enabled: !workspace.ordering })
          }
        >
          {workspace.ordering ? "ປິດຮັບ CF" : "ເປີດຮັບ CF"}
        </Button>
      </div>
      {(error || failed.length > 0) && (
        <div
          role="alert"
          className="space-y-2 rounded-lg border border-destructive p-3"
        >
          <p>
            {error
              ? workspaceErrorMessage(error)
              : `ບັນທຶກບໍ່ສຳເລັດ ${failed.length} ລາຍການ`}
          </p>
          {failed.map((f) => (
            <p key={f.id} className="text-sm">
              {workspace.products.find((p) => p.id === f.id)?.cfCode ??
                "ລາຍການ"}
              : {workspaceErrorMessage(new Error(f.code))}
            </p>
          ))}
          {error instanceof WorkspaceRequestError &&
            Array.isArray(error.details) && (
              <ul className="text-sm">
                {error.details.map((detail) => (
                  <li
                    key={
                      detail.draftProductId ??
                      detail.field ??
                      detail.cfCode ??
                      detail.optionId
                    }
                  >
                    {detail.cfCode ?? detail.optionId ?? "ລາຍການຂັດກັນ"}
                    {detail.draft !== undefined && (
                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                        <div>
                          ງານທີ່ກຽມ:{" "}
                          <code className="break-all">
                            {conflictSummary(detail.draft)}
                          </code>
                        </div>
                        <div>
                          Live / Feed ເດີມ:{" "}
                          <code className="break-all">
                            {conflictSummary(detail.existing)}
                          </code>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          {retryCommand && (
            <Button
              variant="outline"
              className="min-h-11"
              disabled={busy}
              onClick={() => void save(retryCommand.action, retryCommand)}
            >
              ສົ່ງຄຳສັ່ງເດີມອີກຄັ້ງ
            </Button>
          )}
        </div>
      )}
      <div
        className={`grid min-w-0 gap-6 ${tab !== "catalog" ? "xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.55fr)]" : ""}`}
      >
        <Tabs value={tab} onValueChange={setTab} className="min-w-0">
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            <TabsTrigger className="min-h-11" value="products">
              ຄິວຂາຍ
            </TabsTrigger>
            <TabsTrigger className="min-h-11" value="catalog">
              ເພີ່ມສິນຄ້າ
            </TabsTrigger>
            <TabsTrigger className="min-h-11" value="prepare">
              ກຽມ / ຜູກງານ
            </TabsTrigger>
            <TabsTrigger className="min-h-11" value="obs">
              ຈໍ / ລິ້ງ OBS
            </TabsTrigger>
            {has("orders:read") || has("orders:manage") ? (
              <TabsTrigger className="min-h-11" value="orders">
                ອໍເດີ
              </TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="products" className="space-y-4">
            <section
              className="grid grid-cols-2 gap-4 border-b pb-4 text-sm"
              aria-label="ສະຖານະຈໍ ແລະຄິວຖັດໄປ"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <RiEyeLine size={16} aria-hidden /> ກຳລັງຂຶ້ນຈໍ
                </p>
                <p className="mt-1 break-words font-semibold">
                  {active ? `${activeIndex + 1}. ${active.title}` : "ເຊື່ອງຈໍຢູ່"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <RiSkipForwardLine size={16} aria-hidden /> ຄິວຖັດໄປ
                </p>
                <p className="mt-1 break-words font-semibold">
                  {next
                    ? `${workspace.queue.findIndex((p) => p.id === next.id) + 1}. ${next.title}`
                    : "ບໍ່ມີຄິວຖັດໄປ"}
                </p>
              </div>
            </section>
            <Collapsible>
              <div className="flex gap-2">
                <Input
                  aria-label="ຄົ້ນຫາສິນຄ້າໃນຄິວ"
                  placeholder="ຄົ້ນຫາຊື່, SKU, ລະຫັດ CF…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="min-h-11 min-w-0 flex-1"
                />
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="min-h-11 shrink-0 gap-2">
                    <RiFilter3Line size={18} aria-hidden />
                    ຕົວກອງ{filterCount > 0 ? ` (${filterCount})` : ""}
                  </Button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent className="space-y-2 pt-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <WorkspaceSelect
                    label="ການຂາຍ"
                    value={filters.sale}
                    items={[
                      ["all", "ທັງໝົດ"],
                      ["on", "ເປີດຂາຍ"],
                      ["off", "ກຽມໄວ້ / ປິດຂາຍ"],
                    ]}
                    onChange={(sale) => setFilters({ ...filters, sale })}
                  />
                  <WorkspaceSelect
                    label="ຄວາມພ້ອມ"
                    value={filters.readiness}
                    items={[
                      ["all", "ທັງໝົດ"],
                      ["ready", "ພ້ອມຂາຍ"],
                      ["zero", "ສະຕັອກ 0"],
                      ["preorder", "Preorder"],
                    ]}
                    onChange={(readiness) =>
                      setFilters({ ...filters, readiness })
                    }
                  />
                  <WorkspaceSelect
                    label="ສ່ວນຫຼຸດ"
                    value={filters.discount}
                    items={[
                      ["all", "ທັງໝົດ"],
                      ["yes", "ມີສ່ວນຫຼຸດ"],
                      ["no", "ລາຄາປົກກະຕິ"],
                    ]}
                    onChange={(discount) =>
                      setFilters({ ...filters, discount })
                    }
                  />
                  <WorkspaceSelect
                    label="ຄິວ"
                    value={filters.queue}
                    items={[
                      ["all", "ທັງໝົດ"],
                      ["included", "ຢູ່ໃນຄິວ"],
                      ["excluded", "ນຳອອກຈາກຄິວ"],
                    ]}
                    onChange={(queue) => setFilters({ ...filters, queue })}
                  />
                </div>
                {filterCount > 0 && (
                  <Button
                    variant="ghost"
                    className="min-h-11"
                    onClick={() =>
                      setFilters({
                        sale: "all",
                        readiness: "all",
                        discount: "all",
                        queue: "all",
                      })
                    }
                  >
                    ລ້າງຕົວກອງ
                  </Button>
                )}
              </CollapsibleContent>
            </Collapsible>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold">ສິນຄ້າ ({workspace.total})</h2>
              <Button
                variant={managingQueue ? "secondary" : "outline"}
                className="min-h-11"
                aria-pressed={managingQueue}
                onClick={() => setManagingQueue((value) => !value)}
              >
                {managingQueue ? (
                  <RiCheckLine size={18} />
                ) : (
                  <RiDragMove2Line size={18} />
                )}
                {managingQueue ? "ຈັດຄິວແລ້ວ" : "ຈັດຄິວ / ລາຄາ"}
              </Button>
            </div>
            {(managingQueue || selected.length > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                <label
                  htmlFor="workspace-select-visible-products"
                  className="flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm"
                >
                  <Checkbox
                    id="workspace-select-visible-products"
                    aria-label="ເລືອກສິນຄ້າໃນໜ້ານີ້ທັງໝົດ"
                    disabled={busy || workspace.products.length === 0}
                    checked={
                      workspace.products.length > 0 &&
                      workspace.products.every((product) =>
                        selectedIds.has(product.id),
                      )
                        ? true
                        : workspace.products.some((product) =>
                              selectedIds.has(product.id),
                            )
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(checked) =>
                      setSelected((previous) => [
                        ...previous.filter(
                          (item) =>
                            !workspace.products.some(
                              (product) => product.id === item.id,
                            ),
                        ),
                        ...(checked === true ? workspace.products : []),
                      ])
                    }
                  />
                  ເລືອກໜ້ານີ້
                </label>
                <Button
                  variant="outline"
                  className="min-h-11"
                  disabled={busy || selected.length === 0}
                  onClick={() =>
                    setEditor({ products: selected, global: false })
                  }
                >
                  ລາຄາ / ບໍລິການ ({selected.length})
                </Button>
                <Button
                  variant="outline"
                  className="min-h-11"
                  disabled={busy || selected.length === 0}
                  onClick={() =>
                    void save({
                      type: "products",
                      ids: selected.map((product) => product.id),
                      saleEnabled: true,
                    })
                  }
                >
                  ເປີດຂາຍ ({selected.length})
                </Button>
                <Button
                  variant="outline"
                  className="min-h-11"
                  disabled={busy || selected.length === 0}
                  onClick={() =>
                    void save({
                      type: "products",
                      ids: selected.map((product) => product.id),
                      saleEnabled: false,
                    })
                  }
                >
                  ປິດຂາຍ ({selected.length})
                </Button>
                <Button
                  variant="destructive"
                  className="min-h-11"
                  disabled={busy || selected.length === 0}
                  onClick={() => void removeProducts(selected)}
                >
                  <RiDeleteBinLine size={18} />
                  ນຳອອກ ({selected.length})
                </Button>
                {managingQueue && (
                  <Button
                    variant="outline"
                    className="min-h-11"
                    disabled={busy}
                    onClick={() => setEditor({ products: [], global: true })}
                  >
                    ໂປຣທັງງານ
                  </Button>
                )}
                {selected.length > 0 && (
                  <Button
                    variant="ghost"
                    className="min-h-11"
                    onClick={() => setSelected([])}
                  >
                    ລ້າງທີ່ເລືອກ
                  </Button>
                )}
              </div>
            )}
            {managingQueue && (
              <p className="text-muted-foreground text-xs">
                ລາກ ຫຼືໃສ່ລຳດັບໃນຄິວທັງໝົດ. ການຈັດຄິວບໍ່ປ່ຽນສິນຄ້າທີ່ກຳລັງຂຶ້ນຈໍ.
                {" ເປີດຕົວເລືອກເພື່ອແກ້ລາຄາ ຫຼືເລືອກຫຼາຍລາຍການ."}
              </p>
            )}
            <div
              ref={productsScrollRootRef}
              className={`grid max-h-[42rem] gap-2 overflow-auto overscroll-contain ${
                managingQueue || selected.length > 0
                  ? "grid-cols-1"
                  : "grid-cols-2 2xl:grid-cols-3"
              }`}
            >
              {[...groups].map(([groupId, variants]) => {
                const product = variants[0];
                if (!product) return null;
                const position = queuePositions.get(groupId) ?? 0;
                const excluded =
                  workspace.state.excludedProductIds.includes(groupId);
                const selectedCount = variants.filter((variant) =>
                  selectedIds.has(variant.id),
                ).length;
                const minPrice = Math.min(
                  ...variants.map((variant) => variant.salePrice),
                );
                const maxPrice = Math.max(
                  ...variants.map((variant) => variant.salePrice),
                );
                const isActive = active?.id === groupId;
                const actions: WorkspaceProductCardAction[] = [
                  {
                    id: "display",
                    label: isActive ? "ກຳລັງຂຶ້ນຈໍ" : "ຂຶ້ນຈໍ",
                    icon: <RiEyeLine className="size-4" aria-hidden />,
                    disabled: busy || isActive,
                    onSelect: () =>
                      void save({
                        type: "display",
                        productId: groupId,
                      }),
                  },
                ];
                return (
                  <WorkspaceProductCard
                    key={groupId}
                    productId={groupId}
                    name={product.name}
                    imageUrl={product.imageUrl}
                    active={isActive}
                    badge={isActive ? "ຂຶ້ນຈໍຢູ່" : undefined}
                    subtitle={
                      <>
                        ລຳດັບ {position}
                        {excluded ? " · ນອກຄິວ" : ""} ·{" "}
                        {variants
                          .slice(0, 3)
                          .map((variant) => variant.cfCode)
                          .join(" · ")}
                        {variants.length > 3
                          ? ` · +${variants.length - 3}`
                          : ""}
                      </>
                    }
                    price={
                      <>
                        {minPrice === maxPrice
                          ? kip(minPrice)
                          : `${kip(minPrice)} – ${kip(maxPrice)}`}
                        {selectedCount > 0 ? ` · ເລືອກ ${selectedCount}` : ""}
                      </>
                    }
                    variants={variants.map((variant) => ({
                      id: variant.id,
                      selectionLabel: `ເລືອກ ${variant.cfCode}`,
                      content: (
                        <>
                          <div className="flex min-w-0 items-baseline gap-1.5 text-sm">
                            <strong className="shrink-0 text-primary">
                              {variant.cfCode}
                            </strong>
                            <span className="truncate">{variant.label}</span>
                          </div>
                          <p className="truncate text-muted-foreground text-xs">
                            {variant.saleEnabled ? "ເປີດຂາຍ" : "ປິດຂາຍ"} ·{" "}
                            {variant.isPreorder
                              ? "Preorder"
                              : "ຂາຍໄດ້ " +
                                (variant.allocatedQuantity ??
                                  variant.stockRemaining) +
                                " · ເຫຼືອ " +
                                (variant.remaining ?? "ບໍ່ຈຳກັດ")}
                            {variant.optionsInherited
                              ? " · ບໍລິການມາດຕະຖານ"
                              : " · ບໍລິການກຳນົດເອງ"}
                          </p>
                        </>
                      ),
                      mobilePrice: kip(variant.salePrice),
                      desktopPrice: (
                        <>
                          <strong className="text-sm tabular-nums">
                            {kip(variant.salePrice)}
                          </strong>
                          {variant.salePrice < variant.price ? (
                            <p className="text-muted-foreground text-xs">
                              <s>{kip(variant.price)}</s>
                            </p>
                          ) : null}
                        </>
                      ),
                      actions: [
                        {
                          id: "edit",
                          label: "ລາຄາ / ຈຳນວນ / ບໍລິການ",
                          disabled: busy,
                          onSelect: () =>
                            setEditor({
                              products: [variant],
                              global: false,
                            }),
                        },
                        {
                          id: "sale",
                          label: variant.saleEnabled ? "ປິດຂາຍ" : "ເປີດຂາຍ",
                          disabled:
                            busy || (!variant.saleEnabled && !variant.canSell),
                          onSelect: () =>
                            void save({
                              type: "products",
                              ids: [variant.id],
                              saleEnabled: !variant.saleEnabled,
                            }),
                        },
                        {
                          id: "remove",
                          label: "ນຳອອກ",
                          icon: (
                            <RiDeleteBinLine className="size-4" aria-hidden />
                          ),
                          separatorBefore: true,
                          destructive: true,
                          disabled: busy,
                          onSelect: () => void removeProducts([variant]),
                        },
                      ],
                    }))}
                    selectedVariantIds={selectedIds}
                    onSelectionChange={(variantIds, checked) =>
                      setSelected((previous) => [
                        ...previous.filter(
                          (item) => !variantIds.includes(item.id),
                        ),
                        ...(checked
                          ? variants.filter((item) =>
                              variantIds.includes(item.id),
                            )
                          : []),
                      ])
                    }
                    selectionDisabled={busy}
                    actions={actions}
                    draggable={managingQueue && !busy}
                    onDragStart={(event) =>
                      event.dataTransfer.setData("text/plain", groupId)
                    }
                    onDrop={(event) => {
                      event.preventDefault();
                      const productId =
                        event.dataTransfer.getData("text/plain");
                      if (managingQueue && productId && productId !== groupId)
                        void save({
                          type: "move",
                          productId,
                          position,
                        });
                    }}
                    management={
                      managingQueue ? (
                        <div className="flex flex-wrap items-center gap-2 border-t px-4 py-2">
                          <Button
                            variant="outline"
                            className="min-h-11"
                            disabled={busy}
                            onClick={() =>
                              setEditor({
                                products: variants,
                                global: false,
                              })
                            }
                          >
                            ລາຄາ / ບໍລິການທັງສິນຄ້າ
                          </Button>
                          <Button
                            variant="outline"
                            className="size-11"
                            disabled={busy || position === 1}
                            aria-label={`ເລື່ອນ ${product.name} ຂຶ້ນ`}
                            onClick={() =>
                              void save({
                                type: "move",
                                productId: groupId,
                                position: position - 1,
                              })
                            }
                          >
                            <RiArrowUpLine size={18} />
                          </Button>
                          <Button
                            variant="outline"
                            className="size-11"
                            disabled={
                              busy || position === workspace.queue.length
                            }
                            aria-label={`ເລື່ອນ ${product.name} ລົງ`}
                            onClick={() =>
                              void save({
                                type: "move",
                                productId: groupId,
                                position: position + 1,
                              })
                            }
                          >
                            <RiArrowDownLine size={18} />
                          </Button>
                          <label
                            htmlFor={`workspace-position-${groupId}`}
                            className="flex min-h-11 items-center gap-1"
                          >
                            <span className="text-sm">ລຳດັບ</span>
                            <Input
                              key={position}
                              id={`workspace-position-${groupId}`}
                              type="number"
                              defaultValue={position}
                              min={1}
                              max={workspace.queue.length}
                              className="min-h-11 w-20"
                              onBlur={(event) =>
                                void save({
                                  type: "move",
                                  productId: groupId,
                                  position: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                          <Button
                            variant="ghost"
                            className="min-h-11 sm:ml-auto"
                            disabled={busy}
                            onClick={() =>
                              void save({
                                type: "exclude",
                                productId: groupId,
                                excluded: !excluded,
                              })
                            }
                          >
                            {excluded ? "ກັບເຂົ້າຄິວ" : "ນຳອອກຈາກຄິວ"}
                          </Button>
                          <Button
                            variant="destructive"
                            className="min-h-11"
                            disabled={busy}
                            onClick={() => void removeProducts(variants)}
                          >
                            <RiDeleteBinLine size={18} />
                            ນຳສິນຄ້າອອກ
                          </Button>
                        </div>
                      ) : null
                    }
                  />
                );
              })}
              {!groups.size && (
                <div className="col-span-full rounded-xl border border-dashed p-8 text-center">
                  <p>
                    {q || Object.values(filters).some((v) => v !== "all")
                      ? "ບໍ່ພົບສິນຄ້າຕາມເງື່ອນໄຂ"
                      : "ເລີ່ມກຽມງານໂດຍເພີ່ມສິນຄ້າ"}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3 min-h-11"
                    onClick={() => setTab("catalog")}
                  >
                    ເພີ່ມສິນຄ້າ
                  </Button>
                </div>
              )}
              {query.hasNextPage ? (
                <div
                  ref={productsSentinelRef}
                  className="col-span-full flex min-h-10 items-center justify-center text-muted-foreground text-xs"
                  aria-live="polite"
                >
                  {query.isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
                </div>
              ) : null}
            </div>
          </TabsContent>
          <TabsContent value="orders">
            <WorkspaceOrders id={id} />
          </TabsContent>
          <TabsContent value="catalog">
            <WorkspaceCatalog
              workspace={workspace}
              busy={busy}
              save={save}
              applyProductSet={applyProductSet}
              onViewQueue={(name) => {
                setQ(name);
                setFilters({
                  sale: "all",
                  readiness: "all",
                  discount: "all",
                  queue: "all",
                });
                setTab("products");
              }}
            />
          </TabsContent>
          <TabsContent value="prepare">
            <WorkspacePreparation
              workspace={workspace}
              busy={busy}
              save={save}
              onError={setError}
              onConnected={(feedId) =>
                window.location.assign(
                  `/app/feeds/post-products?pageId=${encodeURIComponent(workspace.pageId)}&feedId=${encodeURIComponent(feedId)}`,
                )
              }
            />
          </TabsContent>
          <TabsContent value="obs" className="space-y-5">
            <div className="xl:hidden">{preview}</div>
            <WorkspaceDisplaySettings
              workspace={workspace}
              busy={busy}
              save={save}
            />
          </TabsContent>
        </Tabs>
        <aside
          className={`${tab === "catalog" ? "hidden" : "hidden xl:block"} min-w-0`}
        >
          <div className="sticky top-4 rounded-xl border p-4">{preview}</div>
        </aside>
      </div>
      {editor && (
        <WorkspaceProductEditor
          saveError={
            error
              ? workspaceErrorMessage(error)
              : failed.length
                ? failed
                    .map((f) => workspaceErrorMessage(new Error(f.code)))
                    .join(" · ")
                : null
          }
          workspace={workspace}
          products={editor.products}
          global={editor.global}
          onClose={() => setEditor(null)}
          onSave={save}
          busy={busy}
        />
      )}
      {tab !== "catalog" && (
        <section
          aria-label="ຄວບຄຸມຄິວຂາຍ"
          className="@container/queue-controls sticky bottom-0 z-20 space-y-2 rounded-t-xl border bg-background p-3 pb-[max(12px,env(safe-area-inset-bottom))] shadow-[0_-4px_16px_0_#00000014] xl:hidden"
        >
          <div className="flex min-w-0 items-center justify-between gap-2">
            <div className="min-w-0 text-sm">
              <p className="text-muted-foreground text-xs">ຄິວຖັດໄປ</p>
              <p className="truncate font-semibold" title={next?.title}>
                {next?.title ?? "ບໍ່ມີຄິວຖັດໄປ"}
              </p>
            </div>
            {workspace.feedId && (
              <a
                className={`${buttonVariants({ variant: "ghost", size: "sm" })} min-h-11 shrink-0 gap-1 px-2`}
                href={`/app/feeds/${encodeURIComponent(workspace.feedId)}/monitor-display`}
                target="_blank"
                rel="noreferrer"
              >
                <RiExternalLinkLine size={18} />
                ເປີດໜ້າຈໍ
              </a>
            )}
          </div>
          <div className="grid @[28rem]/queue-controls:grid-cols-4 grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 py-2"
              disabled={busy || !previous}
              title={previous?.title}
              onClick={() =>
                previous &&
                void save({ type: "display", productId: previous.id })
              }
            >
              <RiSkipBackLine size={18} />
              ກ່ອນໜ້າ
            </Button>
            <Button
              className="h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 py-2"
              disabled={busy || !next}
              onClick={() => void save({ type: "next" })}
            >
              <RiSkipForwardLine size={18} />
              ຖັດໄປ
            </Button>
            <Button
              variant="outline"
              className="h-auto min-h-11 min-w-0 whitespace-normal px-2 py-2"
              disabled={busy || !active}
              onClick={() => void save({ type: "skip" })}
            >
              ຂ້າມ / ນຳອອກ
            </Button>
            <Button
              variant="outline"
              className="h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 py-2"
              disabled={busy || !active}
              onClick={() => void save({ type: "display", productId: null })}
            >
              <RiEyeOffLine size={18} />
              ເຊື່ອງຈໍ
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

type Save = (
  action: WorkspaceCommand["action"],
  previous?: WorkspaceCommand,
) => Promise<boolean>;
function WorkspaceCatalog({
  workspace,
  busy,
  save,
  applyProductSet,
  onViewQueue,
}: {
  workspace: WorkspaceSnapshot;
  busy: boolean;
  save: Save;
  applyProductSet: (templateId: string) => Promise<CommandResult | false>;
  onViewQueue: (name: string) => void;
}) {
  const [q, setQ] = useState("");
  const search = useDelayed(q);
  const [readiness, setReadiness] = useState("all");
  const [added, setAdded] = useState("no");
  const [selected, setSelected] = useState<AdminCatalogItem[]>([]);
  const [priceDialogItems, setPriceDialogItems] = useState<AdminCatalogItem[]>(
    [],
  );
  const [addPrice, setAddPrice] = useState<PriceInput>({
    mode: "inherit",
    value: undefined,
  });
  const [notice, setNotice] = useState("");
  const adding = useRef(false);
  const warehouses = usePosWarehousesQuery();
  const [warehouseFilter, setWarehouseFilter] = useState(
    workspace.warehouseId ?? "",
  );
  const params = new URLSearchParams({ q: search, limit: String(pageSize) });
  if (readiness !== "all") params.set("readiness", readiness);
  params.set("added", added);
  if (warehouseFilter) params.set("warehouseId", warehouseFilter);
  const catalog = useInfiniteQuery({
    queryKey: [
      "workspace-catalog",
      workspace.id,
      params.toString(),
      workspace.warehouseId,
    ],
    queryFn: ({ signal, pageParam }) => {
      const pageParams = new URLSearchParams(params);
      pageParams.set("offset", String(pageParam));
      return workspaceRequest<{
        products: AdminCatalogProduct[];
        total: number;
      }>(`/${workspace.id}/catalog?${pageParams}`, undefined, signal);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.products.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    retry: false,
  });
  const catalogData = catalog.data
    ? {
        total: catalog.data.pages[0]?.total ?? 0,
        products: catalog.data.pages.flatMap((page) => page.products),
      }
    : undefined;
  const catalogScope = `${search}\u0000${readiness}\u0000${added}\u0000${warehouseFilter}`;
  useEffect(() => {
    setSelected((previous) => (catalogScope ? [] : previous));
  }, [catalogScope]);
  const selectedIds = new Set(selected.map((item) => item.variantId));
  const toggleSelection = (items: AdminCatalogItem[], checked: boolean) => {
    const next = updateCatalogSelection(
      selectedIds,
      items.map((item) => item.variantId),
      checked,
    );
    if (next.size > 100) {
      setNotice("ເລືອກໄດ້ສູງສຸດ 100 ຕົວເລືອກຕໍ່ຄັ້ງ. ເພີ່ມທີ່ເລືອກກ່ອນ ຫຼືຫຼຸດຈຳນວນລົງ");
      return;
    }
    setSelected(
      [
        ...new Map(
          [...selected, ...items].map((item) => [item.variantId, item]),
        ).values(),
      ].filter((item) => next.has(item.variantId)),
    );
  };
  const addItems = async (
    items: AdminCatalogItem[],
    nextPrice: PriceInput = addPrice,
  ) => {
    if (busy || adding.current || !items.length || items.length > 100)
      return false;
    adding.current = true;
    setNotice("");
    try {
      if (
        await save({
          type: "add",
          variantIds: items.map((item) => item.variantId),
          ...(warehouseFilter ? { warehouseId: warehouseFilter } : {}),
          ...(nextPrice.mode !== "inherit" ? { price: nextPrice } : {}),
        })
      ) {
        const savedIds = new Set(items.map((item) => item.variantId));
        setSelected((previous) =>
          previous.filter((item) => !savedIds.has(item.variantId)),
        );
        setNotice(
          `ເພີ່ມເຂົ້າງານແລ້ວ ${items.length} ຕົວເລືອກ · ໄປຄິວຂາຍເພື່ອຈັດລຳດັບ ຫຼືຂຶ້ນຈໍ`,
        );
        return true;
      }
      setNotice("ເພີ່ມບໍ່ສຳເລັດ — ຍັງເກັບລາຍການທີ່ເລືອກໄວ້. ກວດຂໍ້ຄວາມແຈ້ງເຕືອນດ້ານເທິງ");
      return false;
    } finally {
      adding.current = false;
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold text-lg">ເລືອກສິນຄ້າເຂົ້າ Live / Feed</h2>
        <p className="mt-1 text-muted-foreground text-sm">
          ເພີ່ມທັງສິນຄ້າໄດ້ໃນຄັ້ງດຽວ ຫຼືເປີດຕົວເລືອກເພື່ອເລືອກແຕ່ບາງ SKU. ການເພີ່ມບໍ່ປ່ຽນສິນຄ້າທີ່ກຳລັງຂຶ້ນຈໍ.
        </p>
      </div>
      <WorkspaceProductSets
        pageId={workspace.pageId}
        selectedVariantIds={selected.map((item) => item.variantId)}
        selectedPrice={addPrice}
        onApply={applyProductSet}
        disabled={busy}
      />
      <fieldset
        className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 sm:w-fit"
        aria-label="ສະຖານະການເພີ່ມສິນຄ້າ"
      >
        <Button
          variant={added === "no" ? "secondary" : "ghost"}
          className="min-h-11"
          aria-pressed={added === "no"}
          onClick={() => setAdded("no")}
        >
          ຍັງບໍ່ເພີ່ມ
        </Button>
        <Button
          variant={added === "yes" ? "secondary" : "ghost"}
          className="min-h-11"
          aria-pressed={added === "yes"}
          onClick={() => setAdded("yes")}
        >
          ເພີ່ມເຂົ້າງານແລ້ວ
        </Button>
      </fieldset>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="ຄົ້ນຫາສິນຄ້າໃນຄັງ"
        placeholder="ຊື່, SKU, barcode ຫຼືລະຫັດ CF (ເຊັ່ນ H4)"
        className="min-h-11"
      />
      <div className="grid grid-cols-2 gap-2">
        <WorkspaceSelect
          label="ສາງທີ່ໃຊ້ຂາຍ"
          value={warehouseFilter}
          onChange={setWarehouseFilter}
          items={(warehouses.data?.items ?? []).map((w) => [w.id, w.name])}
        />
        <WorkspaceSelect
          label="ຄວາມພ້ອມ"
          value={readiness}
          onChange={setReadiness}
          items={[
            ["all", "ທັງໝົດ ລວມສະຕັອກ 0"],
            ["ready", "ພ້ອມຂາຍ"],
            ["zero", "ສະຕັອກ 0"],
            ["preorder", "Preorder"],
          ]}
        />
      </div>
      <p className="text-muted-foreground text-sm">
        ສິນຄ້າສະຕັອກ 0 ເພີ່ມເພື່ອກຽມໄດ້ ແລະຈະປິດຂາຍໄວ້
        {" · ເລືອກໄດ້ເທື່ອລະ 100 ລາຍການ"}
        {(search.trim() || readiness !== "all") && (
          <span className="block">
            ສະແດງ ແລະເລືອກສະເພາະ SKU ທີ່ກົງກັບຄຳຄົ້ນ / ຕົວກອງນີ້
          </span>
        )}
        {warehouseFilter !== workspace.warehouseId && (
          <span className="block">
            ເມື່ອເພີ່ມສິນຄ້າ ສາງນີ້ຈະຖືກບັນທຶກເປັນສາງຂາຍຂອງງານ ແລະຄິວຂາຍຈະໃຊ້ສະຕັອກຈາກສາງນີ້
          </span>
        )}
      </p>
      <section
        className="space-y-2 rounded-lg border bg-muted/20 p-3"
        aria-labelledby="workspace-add-price-heading"
      >
        <div>
          <h3 id="workspace-add-price-heading" className="font-medium text-sm">
            ຕັ້ງລາຄາ / ສ່ວນຫຼຸດ ກ່ອນເພີ່ມ
          </h3>
          <p className="text-muted-foreground text-xs">
            ຕັ້ງຄ່າໃຫ້ແລ້ວ ຈາກນັ້ນກົດເພີ່ມສິນຄ້າໄດ້ເລີຍ
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_minmax(0,12rem)_auto] sm:items-end">
          <WorkspaceSelect
            label="ລາຄາທີ່ໃຊ້ຕອນເພີ່ມ"
            value={addPrice.mode}
            onChange={(mode) =>
              setAddPrice({
                mode: mode as PriceInput["mode"],
                value: undefined,
              })
            }
            items={[
              ["inherit", "ໃຊ້ລາຄາ / ໂປຣຂອງງານ"],
              ["sale", "ກອກລາຄາຂາຍຈິງ"],
              ["percent", "ຫຼຸດ %"],
              ["fixed", "ຫຼຸດເປັນກີບ"],
              ["none", "ບໍ່ຫຼຸດລາຄາ"],
            ]}
          />
          {addPrice.mode !== "inherit" && addPrice.mode !== "none" ? (
            <label htmlFor="workspace-add-price" className="space-y-1 text-sm">
              <span>
                {addPrice.mode === "sale"
                  ? "ລາຄາຂາຍຈິງ (ກີບ)"
                  : addPrice.mode === "percent"
                    ? "ສ່ວນຫຼຸດ (%)"
                    : "ສ່ວນຫຼຸດ (ກີບ)"}
              </span>
              <Input
                id="workspace-add-price"
                type="number"
                min={0}
                max={addPrice.mode === "percent" ? 100 : 999999999}
                value={addPrice.value ?? ""}
                onChange={(event) =>
                  setAddPrice({
                    ...addPrice,
                    value:
                      event.target.value === ""
                        ? undefined
                        : Number(event.target.value),
                  })
                }
                className="min-h-11"
              />
            </label>
          ) : null}
          <p className="text-muted-foreground text-xs sm:pb-2">
            ຄ່ານີ້ຈະໃຊ້ກັບທຸກ SKU ທີ່ກົດເພີ່ມຄັ້ງຕໍ່ໄປ
          </p>
        </div>
      </section>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">ສິນຄ້າ ({catalogData?.total ?? 0})</h3>
        <Button
          variant="outline"
          className="min-h-11"
          onClick={() => onViewQueue("")}
        >
          ໄປຄິວຂາຍ
        </Button>
      </div>
      <output
        className={notice ? "block text-sm" : "sr-only"}
        aria-live="polite"
      >
        {notice}
      </output>
      {catalog.isLoading ? (
        <output>ກຳລັງຄົ້ນຫາ…</output>
      ) : catalog.isError ? (
        <p role="alert">ໂຫຼດສິນຄ້າບໍ່ສຳເລັດ</p>
      ) : !catalogData?.products.length ? (
        <p className="p-6 text-center text-muted-foreground">
          ບໍ່ພົບສິນຄ້າ. ລອງປ່ຽນຄຳຄົ້ນ ຫຼືຕົວກອງ
        </p>
      ) : (
        <WorkspaceCatalogCards
          products={catalogData.products}
          selectedItems={added === "yes" ? [] : selected}
          disabled={busy}
          readOnly={added === "yes"}
          filtered={Boolean(search.trim() || readiness !== "all")}
          hasNextPage={Boolean(catalog.hasNextPage)}
          isFetchingNextPage={catalog.isFetchingNextPage}
          onLoadMore={() => void catalog.fetchNextPage()}
          onSelectionChange={toggleSelection}
          onCommit={setPriceDialogItems}
          onClear={() => setSelected([])}
          onViewProduct={(product) => onViewQueue(product.name)}
        />
      )}
      {priceDialogItems.length > 0 ? (
        <WorkspaceCatalogPriceDialog
          items={priceDialogItems}
          initialPrice={addPrice}
          promo={workspace.promo}
          busy={busy}
          onClose={() => setPriceDialogItems([])}
          onConfirm={async (nextPrice) => {
            setAddPrice(nextPrice);
            if (await addItems(priceDialogItems, nextPrice))
              setPriceDialogItems([]);
          }}
        />
      ) : null}
    </div>
  );
}
